import { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { errText } from "../../auth/EmailCode";

const TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function ProfilePhoto({ p, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const { checkAuth } = useAuth();
  const apply = (data) => { onChange("picture", data.picture); onChange("has_photo", data.has_photo); checkAuth(); };

  const upload = async (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) return toast.error("Please choose a JPG, PNG or WebP photo.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Please choose a photo under 5 MB.");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/profile/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      apply(data);
      toast.success("Photo updated.");
    } catch (e) {
      toast.error(errText(e, "We couldn't upload that photo. Please try again."));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const remove = async () => {
    setBusy(true);
    try { apply((await api.delete("/profile/photo")).data); toast.success("Photo removed."); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col items-center gap-1.5 shrink-0">
      <button data-testid="profile-photo-button" onClick={() => input.current?.click()} disabled={busy} aria-label={p.picture ? "Change your photo" : "Add a photo"}
        className="group relative w-20 h-20 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2">
        {p.picture ? <img src={p.picture} alt="" data-testid="profile-photo-img" className="w-20 h-20 rounded-full object-cover border border-line" /> : (
          <span className="w-20 h-20 rounded-full bg-brand-soft text-brand-text flex items-center justify-center text-2xl font-bold" aria-hidden="true">{(p.name || "?").trim().charAt(0).toUpperCase()}</span>
        )}
        <span className="absolute inset-0 rounded-full bg-ink/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" aria-hidden="true">
          {!busy && <Camera className="w-6 h-6 text-white" />}
        </span>
        <span className="absolute -bottom-0.5 -right-0.5 w-8 h-8 rounded-full bg-brand text-onbrand border-2 border-surface flex items-center justify-center" aria-hidden="true">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        </span>
      </button>
      <input ref={input} type="file" accept={TYPES.join(",")} className="hidden" data-testid="profile-photo-input" onChange={(e) => upload(e.target.files?.[0])} />
      {p.has_photo ? (
        <button data-testid="profile-photo-remove" onClick={remove} disabled={busy} className="inline-flex items-center gap-1 text-xs font-medium text-body hover:text-danger"><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Remove</button>
      ) : (
        <span className="text-xs text-faint">{p.picture ? "Change photo" : "Add photo"}</span>
      )}
    </div>
  );
}
